import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeShowIcon,
  EyeHideIcon,
} from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { Absolute } from '@rsos/components/capstone/base';
import {
  exitFullscreen,
  getBrowserFullscreenElementProp,
  toggleFullscreen,
} from '@rsos/utils/fullscreenHelpers';
import {
  useHasCallEnded,
  useSelectedMediaData,
  useSelectedVideoData,
} from '../AdrQuery/selectors/selectors';
import {
  trackMediaBlurUnblurClick,
  trackMediaLiveVideoBlurUnblurClick,
} from '../mediaTrackings';
import {
  CarouselContainer,
  CarouselPreviewContainer,
  IconWrapper,
  LeftArrow,
  RightArrow,
} from './Carousel/Carousel.styles';
import {
  CurrentCarouselItem,
  CarouselPreviewItem,
} from './Carousel/CarouselItems';
import { IframeContainer } from './MediaCard.styles';

const START_INDEX = 0;

const MediaCard = () => {
  const carouselRef = useRef();
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const mediaUploadData = useSelectedMediaData(selectedCallerID);
  const videoData = useSelectedVideoData(selectedCallerID);

  const hasCallEnded = useHasCallEnded(selectedCallerID);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBlurred, setIsBlurred] = useState(true);
  const [isIframeBlurred, setIsIframeBlurred] = useState(true);

  const [currentData, setCurrentData] = useState(mediaUploadData[START_INDEX]);

  const handleSetCurrentData = data => {
    setCurrentData(data);
  };

  const currentIdx = useMemo(() => {
    const foundIdx = mediaUploadData.findIndex(data => {
      return (
        (currentData?.media_thumb_url &&
          data?.media_thumb_url === currentData?.media_thumb_url) ||
        (currentData?.video_url && data?.video_url === currentData?.video_url)
      );
    });
    return foundIdx;
  }, [currentData, mediaUploadData]);

  const handleClickPrev = () => {
    if (currentIdx === 0) {
      // If at the beginning, go to the end
      setCurrentData(mediaUploadData[mediaUploadData.length - 1]);
    } else {
      const foundIdx = mediaUploadData.findIndex(data => {
        return (
          (currentData?.media_thumb_url &&
            data?.media_thumb_url === currentData?.media_thumb_url) ||
          (currentData?.video_url && data?.video_url === currentData?.video_url)
        );
      });
      setCurrentData(mediaUploadData[foundIdx - 1]);
    }
  };

  const handleClickNext = () => {
    if (currentIdx === mediaUploadData.length - 1) {
      // If at the end, go to the beginning
      setCurrentData(mediaUploadData[0]);
    } else {
      const foundIdx = mediaUploadData.findIndex(data => {
        return (
          (currentData?.media_thumb_url &&
            data?.media_thumb_url === currentData?.media_thumb_url) ||
          (currentData?.video_url && data?.video_url === currentData?.video_url)
        );
      });
      setCurrentData(mediaUploadData[foundIdx + 1]);
    }
  };

  const handleBlur = useCallback(() => {
    setIsBlurred(prev => {
      const updateState = !prev;
      trackMediaBlurUnblurClick(updateState);
      return updateState;
    });
  }, []);

  const handleIframeBlur = useCallback(() => {
    setIsIframeBlurred(prev => {
      const updateState = !prev;
      trackMediaLiveVideoBlurUnblurClick(updateState, videoData?.whip_url);
      return updateState;
    });
  }, []);

  /**
   * Determines which 3 thumbnails to show for the preview
   */
  const previewData = useMemo(() => {
    if (mediaUploadData.length === 0) {
      return [];
    } else if (mediaUploadData.length < 3 && mediaUploadData.length > 0) {
      return mediaUploadData;
    } else if (currentIdx >= 0 && currentIdx <= 2) {
      // return the first 3
      return mediaUploadData.slice(0, 3);
    } else if (
      currentIdx <= mediaUploadData.length - 1 &&
      currentIdx >= mediaUploadData.length - 3
    ) {
      // return the last 3
      return mediaUploadData.slice(
        mediaUploadData.length - 3,
        mediaUploadData.length,
      );
    } else {
      // return previous, current and next
      return mediaUploadData.slice(currentIdx - 1, currentIdx + 2);
    }
  }, [mediaUploadData, currentIdx]);

  // TODO: switch to useToggleFullscreen
  const handleFullscreen = useCallback(() => {
    if (carouselRef.current) {
      if (!isFullscreen) {
        toggleFullscreen(carouselRef.current);
      } else {
        exitFullscreen();
      }
    }
  }, [isFullscreen]);

  React.useLayoutEffect(() => {
    document.onfullscreenchange = () => {
      setIsFullscreen(!!document[getBrowserFullscreenElementProp()]);
    };
    return () => {
      document.onfullscreenchange = undefined;
    };
  });

  useEffect(() => {
    if (!currentData) {
      setCurrentData(mediaUploadData[START_INDEX]);
    }
  }, [currentData, mediaUploadData]);

  return (
    <Box px="24px" py="16px">
      {!hasCallEnded && videoData?.whip_url && (
        <Box mb="16px">
          <Text size="normal" fontWeight="bold" color="primaryText" mb="16px">
            Video Call
          </Text>
          {/* Loading states are controlled by the iframe */}
          <Box position="relative" overflow="hidden">
            <IframeContainer isBlurred={isIframeBlurred}>
              <iframe
                src={videoData.whip_url}
                loading="lazy"
                title="Live Stream Video"
                width="100%"
                allow="fullscreen"
                style={{ border: 0, minHeight: '476px' }}
                sandbox
              />
            </IframeContainer>
            <Absolute center bottom="0" right="0">
              <IconWrapper onClick={handleIframeBlur}>
                {isIframeBlurred ? (
                  <EyeHideIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                ) : (
                  <EyeShowIcon
                    width="16px"
                    height="16px"
                    viewboxHeight={16}
                    viewboxWidth={16}
                    padding="30px"
                  />
                )}
              </IconWrapper>
            </Absolute>
          </Box>
        </Box>
      )}

      {mediaUploadData.length > 0 && currentData && (
        <>
          <Text size="normal" fontWeight="bold" color="primaryText" mb="16px">
            Uploaded Media
          </Text>
          <CarouselContainer
            isFullscreen={isFullscreen}
            ref={carouselRef}
            className="CarouselContainer"
          >
            <CurrentCarouselItem
              data={currentData}
              isBlurred={isBlurred}
              isFullscreen={isFullscreen}
              onBlur={handleBlur}
              onFullscreen={handleFullscreen}
            />

            {currentIdx >= 0 && (
              <Text color="secondaryText" pt="8px" textAlign="center">
                Viewing {currentIdx + 1} of {mediaUploadData.length}
              </Text>
            )}

            <Box>
              {mediaUploadData.length > 1 && (
                <CarouselPreviewContainer
                  className="CarouselPreviewContainer"
                  isFullscreen={isFullscreen}
                  showArrows={mediaUploadData.length > 3}
                >
                  {mediaUploadData.length > 3 && (
                    <LeftArrow
                      isFullscreen={isFullscreen}
                      onClick={handleClickPrev}
                    >
                      <Text color="secondaryText">
                        <ChevronLeftIcon
                          height={30}
                          width={30}
                          viewboxHeight={16}
                          viewboxWidth={16}
                        />{' '}
                      </Text>
                    </LeftArrow>
                  )}

                  <Box horizontal overflowX="auto">
                    {/* This should only render 3 max */}
                    {previewData.map(data => {
                      return (
                        <CarouselPreviewItem
                          data={data}
                          highlight={
                            !!(
                              currentData?.media_thumb_url &&
                              data?.media_thumb_url ===
                                currentData?.media_thumb_url
                            ) ||
                            !!(
                              currentData?.video_url &&
                              data?.video_url === currentData?.video_url
                            )
                          }
                          isBlurred={isBlurred}
                          isFullscreen={isFullscreen}
                          key={`${data?.upload_time}_${data?.media_thumb_url}`}
                          onSetCurrentData={handleSetCurrentData}
                        />
                      );
                    })}
                  </Box>
                  {mediaUploadData.length > 3 && (
                    <RightArrow
                      isFullscreen={isFullscreen}
                      onClick={handleClickNext}
                    >
                      <Text color="secondaryText">
                        <ChevronRightIcon
                          height={30}
                          width={30}
                          viewboxHeight={16}
                          viewboxWidth={16}
                        />{' '}
                      </Text>
                    </RightArrow>
                  )}
                </CarouselPreviewContainer>
              )}
            </Box>
          </CarouselContainer>
        </>
      )}
    </Box>
  );
};
export default MediaCard;
